import type { MouseEvent } from 'react';

import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';

import ImageProxy from '@next-image-proxy';

import { Button } from '@uikit';

import type { StoryBlock } from '@shared/api/types/story';
import { ACTION_TYPES } from '@shared/api/types/story';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';

export default function StoryBlocksItem({
  block = {},
  onButtonClick,
  storyTitle,
}: {
  block: StoryBlock;
  onButtonClick: (type?: string, event?: MouseEvent<HTMLElement>, internalRoute?: string) => void;
  storyTitle?: string;
}) {
  const { isAuth } = useAuth();
  const authModal = useAuthModal();
  const isExternalLinkSubAction =
    ([ACTION_TYPES.GO_TO, ACTION_TYPES.GO_TO_AUTH] as string[]).includes(
      block.action?.type ?? '',
    ) && block.action?.externalRoute;
  const extraLinkProps = {
    as: 'a',
    href: block.action?.externalRoute ?? '/',
    target: '_blank',
    rel: ' noopener noreferrer',
  };

  const handleDescriptionClick = (event: MouseEvent<HTMLDivElement>) => {
    const target: any = event.target;

    if (target?.tagName === 'A' && !isAuth) {
      authModal.open();

      event.preventDefault();

      return;
    }
  };

  return (
    <div className="story" key={block.bannerUrl}>
      {block.bannerUrl && (
        <ImageProxy
          className="story-banner"
          src={block.bannerUrl}
          alt={storyTitle || block.title || 'Story banner'}
          width={1920}
          height={1080}
          quality={90}
        />
      )}

      {(block.title || block.description) && (
        <div>
          <h3
            className="story-title"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.title ?? '') }}
          />
          <p
            className="story-description"
            onClick={handleDescriptionClick}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.description ?? '') }}
          />
        </div>
      )}

      {block.bannerBottomUrl && (
        <ImageProxy
          className="story-banner-bottom"
          src={block.bannerBottomUrl}
          alt={storyTitle || block.title || 'Story banner'}
          width={1920}
          height={1080}
          quality={90}
        />
      )}

      {(block.action?.internalRoute || block.action?.externalRoute) && (
        <Button
          variant="secondary"
          onClick={(event) => onButtonClick(block.action?.type, event, block.action?.internalRoute)}
          {...(isExternalLinkSubAction ? (extraLinkProps as any) : {})}
          className={classNames('story-action font-semibold whitespace-nowrap text-lg h-[50px]')}
        >
          {block.action.buttonText || 'Click me'}
        </Button>
      )}
    </div>
  );
}
